import { FormHeader, FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import React, { useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Item, SellByWeightConfig } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useLocaleUtils } from 'common/hooks';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { ItemQuantity } from 'modules/menu/items/types';

type Props = {
  className?: string;
  item: Item;
  itemQuantity: ItemQuantity | undefined;
};

type ItemQuantityProps = {
  inputType?: SellByWeightConfig['input_type'];
  quantity?: number;
};

const Root = styled.div``;

const InventoryViewContainer = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const SectionInfo = styled.div`
  margin-block-start: 8px;
  ${typographyCss.Small};
  font-weight: normal;
  white-space: normal;
`;

const StockRow = styled.div`
  margin-block-start: 12px;
`;

const InventoryItemQuantity = ({ quantity, inputType }: ItemQuantityProps) => {
  const { formatWeightQuantity } = useLocaleUtils();

  return (
    <>
      {inputType === 'grams' && quantity !== undefined
        ? formatWeightQuantity(quantity, {
            unitDisplay: 'narrow',
          })
        : quantity}
    </>
  );
};

const InventoryMngmtDetailsView = ({ className, item, itemQuantity }: Props) => {
  const intl = useIntl();
  const { isRetail } = useContext(MenuFeaturesContext);

  const isBinaryInventory = !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock';

  return (
    <Root className={className}>
      {isRetail && (
        <InventoryViewContainer className={className}>
          <FormHeader>
            <div>
              <FormattedMessage id={'menu-item.inventory-management'} />
              <SectionInfo>
                <FormattedMessage id={'inventory-management.how-to-use'} />
              </SectionInfo>
            </div>
          </FormHeader>
          {item.inventory_mode === 'limited_quantity' && (
            <FormRow label={intl.formatMessage({ id: 'inventory-management.use' })}>
              <FormattedMessage id="menu-item.enabled-false" />
            </FormRow>
          )}
          {item.inventory_mode !== 'limited_quantity' && (
            <>
              <FormRow label={intl.formatMessage({ id: 'inventory-management.type' })}>
                {isBinaryInventory ? (
                  <FormattedMessage id="inventory-management.binary" />
                ) : (
                  <FormattedMessage id="inventory-management.track-count" />
                )}
              </FormRow>

              <FormRow
                label={intl.formatMessage({
                  id: isBinaryInventory ? 'inventory-management.in-stock' : 'inventory.current-stock',
                })}
              >
                {item.inventory_mode === null && <FormattedMessage id="menu-item.enabled-true" />}
                {item.inventory_mode === 'forced_out_of_stock' && <FormattedMessage id="menu-item.enabled-false" />}
                {item.inventory_mode === 'inventory' && (
                  <>
                    <div>
                      <InventoryItemQuantity
                        quantity={itemQuantity?.balance}
                        inputType={item.sell_by_weight_config?.input_type}
                      />
                    </div>
                    <StockRow>
                      <FormattedMessage id="inventory.reserved-stock" />:{' '}
                      <InventoryItemQuantity
                        quantity={itemQuantity?.reserved}
                        inputType={item.sell_by_weight_config?.input_type}
                      />
                    </StockRow>
                    <StockRow>
                      <FormattedMessage id="inventory.available-stock" />:{' '}
                      <InventoryItemQuantity
                        quantity={itemQuantity?.balance}
                        inputType={item.sell_by_weight_config?.input_type}
                      />
                    </StockRow>
                  </>
                )}
              </FormRow>
            </>
          )}
        </InventoryViewContainer>
      )}
      <InventoryViewContainer className={className}>
        <FormHeader>
          <div>
            <FormattedMessage id={'menu-item.limited-quantity'} />
            <SectionInfo>
              <FormattedMessage id={'limited-quantity.how-to-use'} />
            </SectionInfo>
          </div>
        </FormHeader>
        <FormRow label={intl.formatMessage({ id: 'limited-quantity.use' })}>
          {item.inventory_mode === 'limited_quantity' ? (
            <FormattedMessage id={'menu-item.enabled-true'} />
          ) : (
            <FormattedMessage id={'menu-item.enabled-false'} />
          )}
        </FormRow>
        {item.inventory_mode === 'limited_quantity' && (
          <FormRow label={intl.formatMessage({ id: 'inventory.current' })}>
            <div>
              <InventoryItemQuantity
                quantity={itemQuantity?.balance}
                inputType={item.sell_by_weight_config?.input_type}
              />
            </div>
            <StockRow>
              <FormattedMessage id="inventory.reserved-stock" />:{' '}
              <InventoryItemQuantity
                quantity={itemQuantity?.reserved}
                inputType={item.sell_by_weight_config?.input_type}
              />
            </StockRow>
            <StockRow>
              <FormattedMessage id="inventory.available-stock" />:{' '}
              <InventoryItemQuantity
                quantity={itemQuantity?.balance}
                inputType={item.sell_by_weight_config?.input_type}
              />
            </StockRow>
          </FormRow>
        )}
      </InventoryViewContainer>
    </Root>
  );
};

export default InventoryMngmtDetailsView;
